import {useMemo} from "react";
import {useQuery} from "react-query";
import {useTranslation} from "react-i18next";
import type {App} from "crqlar-react-common/dist/models/app/app.model";
import {
    buildCrossSalePreviewLink,
    CROSS_SALE_OPPORTUNITY_APPS,
    CrossSaleOpportunityApp,
} from "crqlar-react-common/dist/models/app/crossSaleOpportunityApps";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useMe} from "../common/useMe";
import {findCrossSaleMarketplaceApp, isCrossSaleAppSubscribed, isCrossSaleInstallation} from "./crossSaleSubscription";

export interface LockedCrossSaleApp {
    opportunity: CrossSaleOpportunityApp;
    /** Marketplace entry, for its localized name and description. Absent if the catalogue lacks the app. */
    app?: App;
    name: string;
    link: string;
}

export const useCrossSaleOpportunities = () => {
    const {i18n} = useTranslation();
    const {availableApps, isReady, marketplaceApps, organizationApps} = useMe();

    // Same key and request as useAllSectors, so the sidebar shares its cache; that hook hides the loading state.
    const sectorsQuery = useQuery("allSectors", () => SectorService.getAllSectors({
        limit: {offset: 0, limit: 1000, sort: [{field: "name", direction: "ASC"}]},
    }), {enabled: isReady});
    const sectors = useMemo(() => sectorsQuery.data?.content ?? [], [sectorsQuery.data?.content]);

    /*
        Nothing is shown as locked until the subscriptions are known: a locked icon flashing in for an app
        the organization owns would be wrong. Without sectors, the sector-typed apps cannot be decided and
        stay hidden rather than risk it.
     */
    const ready = isReady && !sectorsQuery.isLoading;

    const lockedApps = useMemo<LockedCrossSaleApp[]>(() => {
        if (!ready) return [];

        return CROSS_SALE_OPPORTUNITY_APPS
            .filter(opportunity => !(opportunity.sectorType && !sectorsQuery.isSuccess))
            .filter(opportunity => !isCrossSaleAppSubscribed(opportunity, organizationApps, marketplaceApps, sectors))
            .map(opportunity => {
                const app = findCrossSaleMarketplaceApp(opportunity, marketplaceApps);

                return {
                    opportunity,
                    app,
                    name: app?.nameLocalized?.[i18n.language]
                        || app?.nameLocalized?.en
                        || opportunity.displayName,
                    link: buildCrossSalePreviewLink(opportunity),
                };
            });
    }, [ready, sectorsQuery.isSuccess, organizationApps, marketplaceApps, sectors, i18n.language]);

    /** The installation of a subscribed opportunity this user may open, if any. */
    const findPermittedInstallation = (opportunity: CrossSaleOpportunityApp) => availableApps.find(({orgApp}) => (
        isCrossSaleInstallation(opportunity, orgApp, marketplaceApps, sectors)
    ));

    return {ready, lockedApps, findPermittedInstallation};
};
